/**
 * Japanese translation of Travo's privacy policy and terms of use (./en.ts), provided for reference.
 * The English text governs; if the two differ, the English version prevails.
 */
import { LEGAL_NAME, SUPPORT_EMAIL } from '@/shared/site';
import type { LegalCopy } from './types';

const EMAIL = SUPPORT_EMAIL;
const CLOUDFLARE_PRIVACY = 'https://www.cloudflare.com/privacypolicy/';
const GOOGLE_PRIVACY = 'https://policies.google.com/privacy?hl=ja';
const CONTACT = '[本通知について、どのように当社に問い合わせることができますか？](#contact)';

export const legalJa: LegalCopy = {
  eyebrow: '法的情報',
  onThisPage: 'このページの内容',
  inShort: '要点：',
  translationNote:
    'この日本語版は、参考のために提供する翻訳です。英語版が正本であり、日本語版と英語版の内容に相違がある場合は、英語版が優先します。',
  readInEnglish: '英語版を読む',

  privacy: {
    title: 'プライバシーポリシー',
    updated: '最終更新日：2026年10月25日',
    seo: {
      title: 'プライバシーポリシー | Travo',
      description:
        'Travo がお客様の情報をどのように収集、使用、共有、保護するか。旅の自動記録に使う位置情報の取り扱いと、お客様のプライバシー上の権利について説明します。',
      keywords: 'Travo プライバシーポリシー, 位置情報, 旅行記録 プライバシー, データ削除, GDPR, CCPA'
    },
    sections: [
      {
        id: 'intro',
        heading: '本通知について',
        blocks: [
          `本プライバシー通知は、Travo Labs として事業を営む ${LEGAL_NAME}（以下「**当社**」といいます）が、お客様が当社のサービス（以下「**本サービス**」といいます）を利用する際に、お客様の情報をどのように、またなぜ収集、保存、使用および／または共有（以下「**処理**」といいます）する場合があるかを説明するものです。たとえば、次のような場合が該当します。`,
          [
            '当社のモバイルアプリケーション（Travo）、または本プライバシー通知にリンクしている当社のその他のアプリケーションをダウンロードして使用する場合',
            '販売、マーケティング、イベントなど、その他の関連する方法で当社と関わる場合'
          ],
          `**ご質問やご懸念はありますか？**本プライバシー通知をお読みいただくことで、お客様のプライバシー上の権利と選択肢をご理解いただけます。当社の方針および慣行に同意されない場合は、本サービスを利用しないでください。それでもご質問やご懸念がある場合は、${EMAIL} までお問い合わせください。`
        ]
      },
      {
        id: 'summary',
        heading: '要点のまとめ',
        blocks: [
          'この要約は本プライバシー通知の要点を示すものですが、各要点の後にあるリンクをたどるか、目次を使って該当するセクションを探すことで、これらのトピックの詳細をご確認いただけます。',
          '**当社はどのような個人情報を処理しますか？**お客様が本サービスを訪問、利用または閲覧する際、当社は、お客様と当社および本サービスとの関わり方、お客様が行う選択、ならびにお客様が利用する製品および機能に応じて、個人情報を処理することがあります。詳しくは[お客様が当社に開示する個人情報](#collect)をご覧ください。',
          '**当社は機微な個人情報を処理しますか？**当社は、中核となる旅行記録機能を提供するために位置情報を処理します。詳しくは[当社によるお客様の情報の処理方法](#process)をご覧ください。',
          '**当社は第三者から情報を受け取りますか？**当社は、認証のためのソーシャルメディアプラットフォームなど、お客様が連携を選択した第三者サービスから情報を受け取ることがあります。詳しくは[その他の情報源から収集する情報](#collect)をご覧ください。',
          '**当社はお客様の情報をどのように処理しますか？**当社は、本サービスの提供、改善および管理、お客様の旅行履歴の自動記録、お客様の旅行パターンの分析、AI によるおすすめの生成、ならびにお客様とのコミュニケーションのために、お客様の情報を処理します。当社は、正当な法的理由がある場合にのみお客様の情報を処理します。詳しくは[当社によるお客様の情報の処理方法](#process)をご覧ください。',
          '**当社はどのような状況で、どの当事者と個人情報を共有しますか？**当社は、特定の状況において、位置情報サービスプロバイダー、クラウドストレージプロバイダー、AI 処理サービス、アプリ内広告のための Google AdMob などの特定の第三者と情報を共有することがあります。詳しくは[当社がお客様の個人情報をいつ、誰と共有するか](#sharing)をご覧ください。',
          '**当社はお客様の情報をどのように安全に保護していますか？**当社は、お客様の個人情報を保護するための組織的および技術的なプロセスと手順を整備しています。しかし、インターネット上の電子的な送信や情報保存技術が 100% 安全であることは保証できないため、ハッカー、サイバー犯罪者その他の権限のない第三者が当社のセキュリティを破り、お客様の情報を不正に収集、アクセス、窃取または改変することがないと約束または保証することはできません。詳しくは[当社がお客様の情報を安全に保護する方法](#security)をご覧ください。',
          '**お客様にはどのような権利がありますか？**お客様の所在地によっては、適用されるプライバシー法に基づき、お客様は自身の個人情報に関して一定の権利を有する場合があります。詳しくは[お客様のプライバシー上の権利](#rights)をご覧ください。',
          `**お客様はどのように権利を行使できますか？**権利を行使する最も簡単な方法は、${EMAIL} まで当社にご連絡いただくことです。当社は、適用されるデータ保護法に従ってあらゆる請求を検討し、対応します。`
        ]
      },
      {
        id: 'collect',
        heading: '1. 当社はどのような情報を収集しますか？',
        blocks: [
          { h3: 'お客様が当社に開示する個人情報' },
          { inShort: '当社は、お客様が当社に提供する個人情報を収集します。' },
          '当社は、お客様が本サービスに登録する際、当社または当社の製品および本サービスに関する情報の入手に関心を示す際、本サービス上の活動に参加する際、その他お客様が当社に連絡する際に、お客様が任意で当社に提供する個人情報を収集します。',
          '**お客様から提供される個人情報：**当社が収集する個人情報は、お客様と当社および本サービスとの関わりの状況、お客様が行う選択、ならびにお客様が利用する製品および機能によって異なります。当社が収集する個人情報には、以下が含まれる場合があります。',
          ['氏名', 'メールアドレス', '電話番号', 'ユーザー名', 'パスワード', 'プロフィール写真', '連絡先または認証データ'],
          '**機微情報：**当社は、本サービスを提供するために必要な場合、以下の機微情報を処理します。',
          [
            '**位置情報：**お客様がバックグラウンドでの位置情報の記録を有効にした場合、訪れた都市や国を自動的に記録するため、当社は正確な位置情報を継続的に収集します',
            '**写真：**旅の思い出を作成するためにお客様がアップロードを選択した画像'
          ],
          '**ソーシャルメディアのログインデータ：**当社は、Google、Apple その他のソーシャルメディアアカウントなど、お客様の既存のソーシャルメディアアカウントの情報を使用して当社に登録するオプションを提供する場合があります。この方法での登録を選択された場合、当社は下記の「[当社はお客様のソーシャルログインをどのように取り扱いますか？](#social-logins)」というセクションに記載された情報を収集します。',
          'お客様が当社に提供するすべての個人情報は、真実、完全かつ正確でなければならず、お客様はその個人情報に変更があった場合には当社に通知しなければなりません。',
          { h3: '自動的に収集される情報' },
          { inShort: 'インターネットプロトコル（IP）アドレスおよび／またはブラウザやデバイスの特性など、一部の情報は、お客様が本サービスを訪問した際に自動的に収集されます。' },
          '当社は、お客様が本サービスを訪問、利用または閲覧する際に、特定の情報を自動的に収集します。この情報は、お客様個人を特定するもの（氏名や連絡先情報など）ではありませんが、IP アドレス、ブラウザおよびデバイスの特性、オペレーティングシステム、言語設定、参照元 URL、デバイス名、国、位置、お客様が本サービスをいつどのように利用しているかに関する情報、その他の技術情報などのデバイス情報および利用情報が含まれる場合があります。この情報は、主に本サービスのセキュリティと運用の維持、ならびに当社内部の分析およびレポート作成のために必要となります。',
          '当社が収集する情報には、以下が含まれます。',
          [
            '**位置情報：**当社は、正確な位置情報とおおよその位置情報の両方を収集します。正確な位置情報は、GPS その他の技術を使用して、お客様の現在の正確な位置を示す位置情報を収集するものです。おおよその位置情報は、お客様のおおよその位置を示します。当社が収集する情報の量は、お客様が本サービスへのアクセスに使用するデバイスの種類と設定によって異なります。お客様は、情報へのアクセスを拒否するか、デバイスの位置情報設定を無効にすることにより、当社によるこの情報の収集をオプトアウトすることができます。ただし、位置情報の記録は Travo の機能の中核であるため、オプトアウトを選択した場合、本サービスの一部の機能を利用できなくなることがあります。',
            '**ユーザー ID およびデバイス ID：**当社は、製品のパーソナライズ、アプリの機能および分析を可能にするため、ユーザー ID（お客様の身元に紐付けられ、トラッキング目的で使用されます）およびデバイス ID を含む一意の識別子を収集します。',
            '**利用データ：**当社は、お客様が本サービスをどのように利用しているかを把握し、分析、製品のパーソナライズおよびアプリの機能改善を行うため、お客様の製品とのやり取りに関する情報その他の利用データを収集します。',
            '**診断データ：**当社は、本サービスの安定性とパフォーマンスを分析し改善するため、クラッシュデータ、パフォーマンスデータその他の診断情報を収集します。このデータは分析目的でのみ使用されます。',
            '**ログおよび利用データ：**ログおよび利用データとは、お客様が本サービスにアクセスまたは利用した際に当社のサーバーが自動的に収集し、ログファイルに記録する、サービス関連、診断、利用およびパフォーマンスに関する情報です。お客様と当社との関わり方によって、このログデータには、IP アドレス、デバイス情報、ブラウザの種類と設定、本サービスにおけるお客様の活動に関する情報（お客様の利用に関連する日時のタイムスタンプ、閲覧したページやファイル、検索、利用した機能などお客様が行ったその他の操作など）、デバイスのイベント情報（システムアクティビティ、エラーレポート（「クラッシュダンプ」と呼ばれることもあります）、ハードウェア設定など）が含まれる場合があります。',
            '**デバイスデータ：**当社は、お客様が本サービスへのアクセスに使用する携帯電話、タブレットその他のデバイスに関する情報などのデバイスデータを収集します。使用するデバイスによっては、このデバイスデータには、IP アドレス（またはプロキシサーバー）、デバイスおよびアプリケーションの識別番号、位置、ブラウザの種類、ハードウェアのモデル、インターネットサービスプロバイダーおよび／または携帯通信事業者、オペレーティングシステム、システム構成情報などの情報が含まれる場合があります。'
          ],
          { h3: 'その他の情報源から収集する情報' },
          { inShort: '当社は、お客様が当社のアプリに連携した第三者サービスから、限られたデータを収集することがあります。' },
          '当社は、以下のようなその他の情報源からお客様に関する情報を取得することがあります。',
          [
            '**地図および位置情報サービス**（例：Mapbox、OpenStreetMap）：GPS 座標を都市名および国名に変換するため',
            '**ソーシャルメディアプラットフォーム**：お客様がソーシャルログインによる認証を選択した場合',
            '**クラウドストレージサービス**：お客様の旅行データと写真が安全に保存される場所'
          ]
        ]
      },
      {
        id: 'process',
        heading: '2. 当社はお客様の情報をどのように処理しますか？',
        blocks: [
          { inShort: '当社は、本サービスの提供、改善および管理、お客様とのコミュニケーション、セキュリティおよび不正防止、ならびに法令遵守のために、お客様の情報を処理します。また、お客様の同意を得て、その他の目的のためにお客様の情報を処理することもあります。' },
          '**当社は、お客様と本サービスとの関わり方に応じて、以下を含むさまざまな理由でお客様の個人情報を処理します。**',
          [
            '**アカウントの作成と認証を容易にし、その他ユーザーアカウントを管理するため：**当社は、お客様がアカウントを作成してログインできるように、またアカウントを正常に機能する状態に保つために、お客様の情報を処理することがあります。',
            '**お客様の旅行履歴を自動的に記録するため：**当社は、お客様が訪れた都市や国を自動的に記録し、各地に滞在した日数を計算し、お客様の旅の包括的なタイムラインを作成するため、バックグラウンドでお客様の位置情報を処理します。',
            '**お客様の旅行パターンを分析するため：**当社は、訪問した国の総数、訪れた都市、旅行の頻度、その他お客様の旅行行動に関する洞察などの統計を提供するため、お客様の旅行履歴データを処理します。',
            '**AI によるおすすめを生成するため：**当社は、お客様の旅のスタイルや好みに基づいてパーソナライズされた旅行先のおすすめを提供するため、お客様の旅行履歴とパターンを処理することがあります。',
            '**旅の思い出を作成・管理するため：**当社は、お客様が整理された視覚的な旅の記録を作成できるよう、お客様がアップロードした写真と関連する位置情報を処理します。',
            '**地図による可視化を可能にするため：**当社は、お客様が訪れた場所をインタラクティブな地図上に表示し、国境を示すために、お客様の位置情報を処理します。',
            '**お客様に管理上の情報を送信するため：**当社は、当社の製品およびサービスの詳細、当社の規約および方針の変更、その他これらに類する情報をお客様に送信するため、お客様の情報を処理することがあります。',
            '**フィードバックを依頼するため：**当社は、フィードバックを依頼するため、また本サービスのご利用についてお客様に連絡するために必要な場合、お客様の情報を処理することがあります。',
            '**ターゲティング広告およびマーケティングのため：**当社は、お客様の設定および適用法に従い、開発者の広告またはマーケティング、製品のパーソナライズ、ならびに第三者の広告の目的で、お客様の情報（氏名、メールアドレス、ユーザー ID を含みます）を処理することがあります。このデータはお客様の身元に紐付けられ、トラッキング目的で使用されることがあります。',
            '**マーケティングおよびプロモーションに関する連絡をお送りするため：**当社は、お客様のマーケティングに関する設定に沿う場合、お客様が当社に送信した個人情報を当社のマーケティング目的で処理することがあります。お客様は、いつでも当社のマーケティングメールの受信をオプトアウトできます。詳しくは、下記の「[お客様にはどのようなプライバシー上の権利がありますか？](#rights)」をご覧ください。',
            '**本サービスを保護するため：**当社は、不正行為の監視および防止を含め、本サービスの安全とセキュリティを維持する取り組みの一環として、お客様の情報を処理することがあります。',
            '**利用傾向を把握するため：**当社は、本サービスがどのように利用されているかをよりよく理解し、本サービスを改善するため、お客様による本サービスの利用方法に関する情報を処理することがあります。',
            '**個人の生命に関する利益を守り、または保護するため：**当社は、危害を防止する場合など、個人の生命に関する利益を守り、または保護するために必要な場合、お客様の情報を処理することがあります。'
          ]
        ]
      },
      {
        id: 'legal-bases',
        heading: '3. 当社はどのような法的根拠に基づいてお客様の情報を処理しますか？',
        blocks: [
          { inShort: '当社は、必要であると判断し、かつ適用法に基づく正当な法的理由（すなわち法的根拠）がある場合にのみ、お客様の個人情報を処理します。たとえば、お客様の同意がある場合、法令を遵守するため、契約を締結しまたは契約上の義務を履行するためにお客様にサービスを提供するため、お客様の権利を保護するため、または当社の正当な事業上の利益を実現するためなどです。' },
          { h3: 'EU または英国に所在するお客様には、このセクションが適用されます。' },
          '一般データ保護規則（GDPR）および英国 GDPR は、当社がお客様の個人情報を処理するために依拠する正当な法的根拠を説明することを当社に求めています。そのため、当社は、お客様の個人情報を処理するにあたり、以下の法的根拠に依拠することがあります。',
          [
            '**同意：**お客様が特定の目的のためにお客様の個人情報を使用する許可（すなわち同意）を当社に与えた場合、当社はお客様の情報を処理することがあります。お客様はいつでも同意を撤回できます。詳しくは下記の[同意の撤回](#rights)をご覧ください。',
            '**契約の履行：**当社は、本サービスの提供を含め、お客様に対する当社の契約上の義務を履行するために必要であると判断した場合、またはお客様との契約締結前にお客様の要請に応じる場合、お客様の個人情報を処理することがあります。',
            '**正当な利益：**当社は、当社の正当な事業上の利益を達成するために合理的に必要であると判断し、かつその利益がお客様の利益ならびに基本的な権利および自由を上回らない場合、お客様の情報を処理することがあります。たとえば、当社は、記載された目的の一部のため、すなわち、ユーザーの関心を引きつけ維持できるよう本サービスがどのように利用されているかを分析して改善すること、問題を診断しおよび／または不正行為を防止すること、ならびにユーザーエクスペリエンスを改善できるようユーザーが当社の製品およびサービスをどのように利用しているかを把握することのために、お客様の個人情報を処理することがあります。',
            '**法的義務：**当社は、法執行機関や規制当局への協力、当社の法的権利の行使もしくは防御、または当社が関与する訴訟における証拠としてのお客様の情報の開示など、当社の法的義務を遵守するために必要であると判断した場合、お客様の情報を処理することがあります。',
            '**生命に関する利益：**当社は、いずれかの人の安全に対する潜在的な脅威が関わる状況など、お客様または第三者の生命に関する利益を保護するために必要であると判断した場合、お客様の情報を処理することがあります。'
          ],
          { h3: 'カナダに所在するお客様には、このセクションが適用されます。' },
          '当社は、お客様が特定の目的のためにお客様の個人情報を使用する具体的な許可（すなわち明示的な同意）を当社に与えた場合、またはお客様の許可が推定できる状況（すなわち黙示的な同意）において、お客様の情報を処理することがあります。お客様はいつでも[同意を撤回](#rights)できます。',
          '一部の例外的なケースでは、適用法に基づき、お客様の同意なしにお客様の情報を処理することが法的に認められる場合があります。たとえば、次のような場合です。',
          [
            '収集が明らかに個人の利益になり、適時に同意を得ることができない場合',
            '調査ならびに不正の検知および防止のため',
            '一定の条件を満たすことを前提とした事業取引のため',
            '証人の供述に含まれており、保険金請求の査定、処理または解決のために収集が必要な場合',
            '負傷者、病人または死亡者の身元確認および近親者との連絡のため',
            '個人が金銭的虐待の被害者である、被害者であった、または被害者となるおそれがあると信じるに足る合理的な根拠がある場合',
            '同意を得て収集および使用すると情報の入手可能性または正確性が損なわれると合理的に予想され、かつその収集が契約違反またはカナダもしくは州の法律違反の調査に関連する目的のために合理的である場合',
            '記録の提出に関する召喚状、令状、裁判所命令または裁判所規則を遵守するために開示が必要な場合',
            '個人がその雇用、事業または職業の過程で作成したものであり、収集がその情報の作成目的と一致している場合',
            '収集が専ら報道、芸術または文学の目的のためである場合',
            '情報が公開されており、かつ規則で指定されている場合'
          ]
        ]
      },
      {
        id: 'sharing',
        heading: '4. 当社はいつ、誰とお客様の個人情報を共有しますか？',
        blocks: [
          { inShort: '当社は、このセクションに記載する特定の状況において、および／または以下の第三者と情報を共有することがあります。' },
          '**ベンダー、コンサルタントその他の第三者サービスプロバイダー：**当社は、当社のために、または当社に代わってサービスを提供し、その業務を行うために当該情報へのアクセスを必要とする第三者のベンダー、サービスプロバイダー、請負業者または代理人（以下「**第三者**」といいます）とお客様のデータを共有することがあります。当社は第三者との間で、お客様の個人情報の保護に役立つよう設計された契約を締結しています。これは、第三者が当社の指示なしにお客様の個人情報について何らの行為も行うことができないことを意味します。また、第三者は、当社以外のいかなる組織ともお客様の個人情報を共有しません。さらに、第三者は、当社に代わって保有するデータを保護し、当社が指示する期間そのデータを保持することを約束しています。当社が個人情報を共有することがある第三者は、以下のとおりです。',
          [
            '**クラウドコンピューティングおよびストレージサービス：**Google Firebase および Google Cloud（認証、データベース、プッシュ通知およびクラッシュレポート）',
            '**位置情報および地図サービス：**Mapbox（都市・場所の検索および地図の可視化）、OpenStreetMap Nominatim（逆ジオコーディング）、Google Maps API（地図表示および国境）',
            '**AI および機械学習サービス：**Google Gemini、Anthropic Claude（おすすめの生成）',
            '**ユーザーアカウントの登録および認証：**Google Sign-In、Apple Sign-In',
            '**分析サービス：**Google Analytics for Firebase および Mixpanel（モバイルアプリ）、Cloudflare Web Analytics（本ウェブサイト）',
            '**広告サービス：**Google AdMob（Travo アプリでの広告の表示および測定）'
          ],
          'また、当社は以下の状況においてお客様の個人情報を共有する必要が生じる場合があります。',
          [
            '**事業譲渡：**当社は、合併、会社資産の売却、資金調達、または当社事業の全部もしくは一部の他社による買収に関連して、またはその交渉中に、お客様の情報を共有または移転することがあります。',
            '**関連会社：**当社は、当社の関連会社とお客様の情報を共有することがあり、その場合、当該関連会社に本プライバシー通知を遵守するよう求めます。関連会社には、当社の親会社、ならびに子会社、合弁事業パートナー、または当社が支配するもしくは当社と共通の支配下にあるその他の会社が含まれます。'
          ]
        ]
      },
      {
        id: 'cookies',
        heading: '5. 当社は Cookie その他のトラッキング技術を使用していますか？',
        blocks: [
          { inShort: '当社自身は Cookie を設定しません。本ウェブサイトが利用するサービスがお客様の IP アドレスを受け取ることがあり、また動画プレーヤーは、お客様が再生した場合に Cookie を設定することがあります。Travo アプリは Google AdMob の広告を表示し、お客様の同意の範囲内で端末および広告に関するデータを使用します。' },
          'www.travolabs.com は Cloudflare によってホスティングされており、Cloudflare はサイトの配信と保護のために IP アドレスを処理します。アクセス数は、Cookie を設定しない Cloudflare Web Analytics で計測しています。フォントは Google Fonts から読み込まれるため、ページの読み込み時に Google がお客様の IP アドレスを受け取ります。',
          '当社自身は Cookie を設定しません。Travo のページでデモ動画を再生すると、動画は YouTube から読み込まれ、YouTube は Google のポリシーに基づいて Cookie を設定することがあります。詳しくは[Cloudflare プライバシーポリシー](' + CLOUDFLARE_PRIVACY + ')および[Google プライバシーポリシー](' + GOOGLE_PRIVACY + ')をご覧ください。',
          'モバイルアプリケーションは、モバイル特有のトラッキング技術を使用することがあります。Travo アプリは、分析およびクラッシュレポートのために、「[当社はどのような情報を収集しますか？](#collect)」に記載された識別子と利用データを使用します。',
          'Travo アプリは Google AdMob の広告も表示します。広告の表示と効果測定、および不正防止のため、Google はお客様の IP アドレス、端末およびアプリの情報、広告とのやり取り、ならびに端末の広告識別子（iPhone では、お客様がトラッキングを許可した場合に限ります）を受け取ることがあります。',
          '広告が読み込まれる前に、アプリはお客様の所在地の法律が求める同意を取得します。欧州経済領域、英国およびスイスでは Google の同意フォームを表示し、続いて iPhone では Apple の App Tracking Transparency（App のトラッキングの透明性）の確認を表示します。選択内容は後から、アプリのアカウント画面の「プライバシー設定」（提供されている場合）、iOS の「設定」→「プライバシーとセキュリティ」→「トラッキング」、または Android 端末の広告設定で変更できます。詳しくは[Google のサービスを使用するサイトやアプリから収集した情報の Google による使用](https://policies.google.com/technologies/partner-sites?hl=ja)をご覧ください。'
        ]
      },
      {
        id: 'social-logins',
        heading: '6. 当社はお客様のソーシャルログインをどのように取り扱いますか？',
        blocks: [
          { inShort: 'お客様がソーシャルメディアアカウントを使用して本サービスへの登録またはログインを選択した場合、当社はお客様に関する一定の情報にアクセスできる場合があります。' },
          '本サービスでは、第三者のソーシャルメディアアカウントの情報（Google や Apple のログイン情報など）を使用して登録およびログインすることができます。お客様がこの方法を選択した場合、当社はお客様のソーシャルメディアプロバイダーからお客様に関する一定のプロフィール情報を受け取ります。当社が受け取るプロフィール情報は、該当するソーシャルメディアプロバイダーによって異なる場合がありますが、多くの場合、お客様の氏名、メールアドレス、プロフィール写真のほか、お客様が当該ソーシャルメディアプラットフォーム上で公開を選択したその他の情報が含まれます。',
          '当社は、受け取った情報を、本プライバシー通知に記載された目的、または関連する本サービス上でお客様に明示されたその他の目的のためにのみ使用します。なお、当社は、お客様の第三者ソーシャルメディアプロバイダーによるお客様の個人情報のその他の利用について管理しておらず、責任を負いません。同プロバイダーがお客様の個人情報をどのように収集、使用および共有するか、また同プロバイダーのサイトやアプリでプライバシー設定をどのように行うことができるかを理解するために、同プロバイダーのプライバシー通知をご確認いただくことをお勧めします。'
        ]
      },
      {
        id: 'retention',
        heading: '7. 当社はお客様の情報をどのくらいの期間保持しますか？',
        blocks: [
          { inShort: '当社は、法律で別段の定めがない限り、本プライバシー通知に記載された目的を達成するために必要な期間、お客様の情報を保持します。' },
          '当社は、法律によってより長い保持期間が要求または許可されている場合（税務、会計その他の法的要件など）を除き、本プライバシー通知に定める目的のために必要な期間に限り、お客様の個人情報を保持します。本通知に定めるいずれの目的においても、ユーザーのアカウントの終了後90日を超えてお客様の個人情報を保持する必要はありません。',
          'お客様の個人情報を処理する継続的かつ正当な事業上の必要がなくなった場合、当社はその情報を削除または匿名化します。それが不可能な場合（たとえば、お客様の個人情報がバックアップアーカイブに保存されている場合など）には、削除が可能になるまで、お客様の個人情報を安全に保管し、それ以上の処理から隔離します。',
          '**旅行履歴データ：**お客様の位置情報の記録データ、旅行統計および関連する思い出は、継続的な旅行記録サービスを提供するため、お客様のアカウントが有効である限り保持されます。アカウントの削除時には、このデータは90日以内に完全に削除されます。'
        ]
      },
      {
        id: 'security',
        heading: '8. 当社はお客様の情報をどのように安全に保護していますか？',
        blocks: [
          { inShort: '当社は、組織的および技術的なセキュリティ対策の体制を通じて、お客様の個人情報の保護に努めています。' },
          '当社は、当社が処理する個人情報のセキュリティを保護するために設計された、適切かつ合理的な技術的および組織的セキュリティ対策を講じています。しかし、当社の保護措置およびお客様の情報を守るための取り組みにもかかわらず、インターネット上の電子的な送信や情報保存技術が 100% 安全であることは保証できないため、ハッカー、サイバー犯罪者その他の権限のない第三者が当社のセキュリティを破り、お客様の情報を不正に収集、アクセス、窃取または改変することがないと約束または保証することはできません。当社はお客様の個人情報の保護に最善を尽くしますが、本サービスとの間の個人情報の送信は、お客様ご自身の責任において行われるものとします。本サービスへのアクセスは、安全な環境でのみ行ってください。',
          '**当社が実施するセキュリティ対策には、以下が含まれます。**',
          [
            '転送中および保存時のデータの暗号化',
            '安全な認証の仕組み',
            '定期的なセキュリティ評価',
            '業務上知る必要がある者に限定した個人情報へのアクセス',
            'エンタープライズグレードのプロバイダーによる安全なクラウドストレージ'
          ]
        ]
      },
      {
        id: 'minors',
        heading: '9. 当社は未成年者から情報を収集していますか？',
        blocks: [
          { inShort: '当社は、18歳未満の子どもから故意にデータを収集したり、18歳未満の子どもを対象にマーケティングを行ったりすることはありません。' },
          `当社は、18歳未満の子どもに故意にデータを求めたり、18歳未満の子どもを対象にマーケティングを行ったりすることはありません。本サービスを利用することにより、お客様は、自身が18歳以上であること、または当該未成年者の親もしくは保護者であり、当該被扶養未成年者による本サービスの利用に同意していることを表明するものとします。18歳未満のユーザーの個人情報が収集されたことが判明した場合、当社はそのアカウントを無効化し、当該データを当社の記録から速やかに削除するための合理的な措置を講じます。当社が18歳未満の子どもから収集した可能性のあるデータにお気づきの場合は、${EMAIL} までご連絡ください。`
        ]
      },
      {
        id: 'rights',
        heading: '10. お客様にはどのようなプライバシー上の権利がありますか？',
        blocks: [
          { inShort: '欧州経済領域（EEA）、英国（UK）、スイス、カナダなど一部の地域では、お客様は自身の個人情報へのより広いアクセスと管理を可能にする権利を有します。お客様はいつでも自身のアカウントを確認、変更または解約することができます。' },
          `一部の地域（EEA、英国、スイス、カナダなど）では、お客様は適用されるデータ保護法に基づき一定の権利を有します。これには、(i) お客様の個人情報へのアクセスを請求し、その写しを取得する権利、(ii) 訂正または消去を請求する権利、(iii) お客様の個人情報の処理を制限する権利、(iv) 該当する場合、データポータビリティの権利、および (v) 自動化された意思決定の対象とならない権利が含まれる場合があります。また、一定の状況においては、お客様の個人情報の処理に異議を述べる権利を有する場合もあります。このような請求は、下記の「${CONTACT}」セクションに記載された連絡先を使用して当社に連絡することにより行うことができます。`,
          '当社は、適用されるデータ保護法に従ってあらゆる請求を検討し、対応します。',
          'EEA または英国に所在するお客様で、当社がお客様の個人情報を違法に処理しているとお考えの場合は、[加盟国のデータ保護機関](https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm)または[英国のデータ保護機関](https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/)に苦情を申し立てる権利もあります。',
          'スイスに所在するお客様は、[連邦データ保護・情報コミッショナー](https://www.edoeb.admin.ch/edoeb/en/home.html)に連絡することができます。',
          `**同意の撤回：**当社がお客様の個人情報の処理をお客様の同意（適用法に応じて明示的な同意および／または黙示的な同意である場合があります）に依拠している場合、お客様はいつでも同意を撤回する権利を有します。お客様は、下記の「${CONTACT}」セクションに記載された連絡先を使用して当社に連絡するか、デバイスの設定またはアプリ内で位置情報の記録を無効にすることにより、いつでも同意を撤回できます。`,
          'ただし、同意の撤回は、撤回前に行われた処理の適法性に影響を与えるものではなく、また適用法が認める場合には、同意以外の適法な処理根拠に依拠して行われるお客様の個人情報の処理にも影響を与えないことにご注意ください。',
          `**マーケティングおよびプロモーションに関する連絡のオプトアウト：**お客様は、当社が送信するメール内の配信停止リンクをクリックするか、下記の「${CONTACT}」セクションに記載された連絡先を使用して当社に連絡することにより、いつでも当社のマーケティングおよびプロモーションに関する連絡の配信を停止できます。その後、お客様はマーケティングリストから削除されます。ただし、当社は引き続きお客様と連絡を取る場合があります。たとえば、お客様のアカウントの管理と利用に必要なサービス関連のメッセージの送信、サービスに関するご依頼への対応、その他マーケティング以外の目的のためです。`,
          { h3: 'アカウント情報' },
          'お客様のアカウント内の情報をいつでも確認もしくは変更し、またはアカウントを解約したい場合は、以下の方法をご利用いただけます。',
          ['Travo モバイルアプリケーション内のアカウント設定にアクセスする', '記載された連絡先情報を使用して当社に連絡する'],
          'お客様からアカウント解約の請求を受けた場合、当社は、お客様のアカウントと情報を当社の稼働中のデータベースから無効化または削除します。ただし、不正の防止、問題のトラブルシューティング、調査への協力、当社の法的条件の執行および／または適用される法的要件の遵守のため、一部の情報を当社のファイルに保持する場合があります。',
          '**位置情報：**お客様は、デバイスの設定またはアプリ内でいつでも位置情報の記録を無効にすることができます。ただし、位置情報の記録を無効にすると、旅行の自動記録という中核機能が動作しなくなります。',
          `お客様のプライバシー上の権利についてご質問やご意見がある場合は、${EMAIL} までメールでお問い合わせください。`
        ]
      },
      {
        id: 'dnt',
        heading: '11. Do-Not-Track 機能のための制御',
        blocks: [
          'ほとんどのウェブブラウザ、ならびに一部のモバイルオペレーティングシステムおよびモバイルアプリケーションには、お客様のオンライン閲覧活動に関するデータを監視・収集されたくないというプライバシー上の希望を示すために有効化できる Do-Not-Track（「DNT」）機能または設定が含まれています。現時点では、DNT シグナルを認識し実装するための統一的な技術標準は確定していません。そのため、当社は現在、DNT ブラウザシグナルや、オンラインでトラッキングされないというお客様の選択を自動的に伝達するその他の仕組みには対応していません。将来、当社が従わなければならないオンライントラッキングの標準が採用された場合は、本プライバシー通知の改訂版においてその取り扱いをお知らせします。'
        ]
      },
      {
        id: 'us-residents',
        heading: '12. 米国居住者には特有のプライバシー上の権利がありますか？',
        blocks: [
          { inShort: 'カリフォルニア州、コロラド州、コネチカット州、ユタ州またはバージニア州の居住者である場合、お客様には自身の個人情報へのアクセスに関して特定の権利が付与されます。' },
          { h3: '当社はどのようなカテゴリーの個人情報を収集していますか？' },
          '当社は、過去12か月間に以下のカテゴリーの個人情報を収集しました。',
          {
            table: {
              head: ['カテゴリー', '例', '収集の有無'],
              rows: [
                ['A. 識別子', '本名、別名、メールアドレス、アカウント名、一意の識別子などの連絡先詳細', 'はい'],
                ['B. カリフォルニア州顧客記録法に定義される個人情報', '氏名、連絡先情報', 'はい'],
                ['C. 州法または連邦法に基づく保護対象の分類特性', '性別および生年月日', 'いいえ'],
                ['D. 商業情報', '取引情報、購入履歴、財務の詳細および支払情報', 'いいえ'],
                ['E. 生体情報', '指紋および声紋', 'いいえ'],
                ['F. インターネットその他の類似のネットワーク活動', '閲覧履歴、検索履歴、オンライン行動、興味関心データ、当社アプリケーションとのやり取り', 'はい'],
                ['G. 位置情報', 'バックグラウンドでの記録が有効な場合に継続的に記録される、デバイスの正確な位置', 'はい'],
                ['H. 音声、電子、視覚、熱、嗅覚その他の類似の情報', '旅の思い出を作成するためにユーザーがアップロードした写真', 'はい'],
                ['I. 職業または雇用に関連する情報', '業務上の連絡先詳細', 'いいえ'],
                ['J. 教育情報', '学籍記録および名簿情報', 'いいえ'],
                ['K. 収集した個人情報から導き出される推論', 'AI によるおすすめのために、好み、特性および旅行パターンに関するプロファイルを作成する目的で、収集した情報から導き出される推論', 'はい'],
                ['L. 機微な個人情報', '正確な位置情報', 'はい']
              ]
            }
          },
          '当社は、収集した個人情報を、本サービスを提供するために必要な範囲で、または以下の期間、使用および保持します。',
          [
            'カテゴリー A：ユーザーが当社のアカウントを保有している限り',
            'カテゴリー B：ユーザーが当社のアカウントを保有している限り',
            'カテゴリー F：ユーザーが当社のアカウントを保有している限り',
            'カテゴリー G：ユーザーが当社のアカウントを保有している限り',
            'カテゴリー H：ユーザーが当社のアカウントを保有している限り',
            'カテゴリー K：ユーザーが当社のアカウントを保有している限り',
            'カテゴリー L：ユーザーが当社のアカウントを保有している限り'
          ],
          '当社は、以下に関連して、お客様が対面、オンライン、電話または郵便で当社とやり取りする場合に、これらのカテゴリー以外のその他の個人情報を収集することもあります。',
          [
            'カスタマーサポート窓口を通じてサポートを受ける場合',
            '顧客アンケートやコンテストに参加する場合',
            '本サービスの提供を円滑にし、お客様のお問い合わせに対応する場合'
          ],
          { h3: '当社はお客様の個人情報をどのように使用・共有しますか？' },
          '当社によるお客様の個人情報の使用方法については、「[当社はお客様の情報をどのように処理しますか？](#process)」セクションをご覧ください。',
          { h3: 'お客様の情報が他者と共有されることはありますか？' },
          '当社は、当社と各サービスプロバイダーとの間の書面による契約に従い、お客様の個人情報をサービスプロバイダーに開示することがあります。当社による個人情報の開示方法の詳細については、「[当社はいつ、誰とお客様の個人情報を共有しますか？](#sharing)」セクションをご覧ください。',
          '当社は、技術開発および実証のための社内調査の実施など、当社自身の事業目的のためにお客様の個人情報を使用することがあります。これはお客様の個人情報の「販売」とはみなされません。',
          '当社は、過去12か月間に、事業目的または商業目的で第三者に個人情報を販売または共有したことはありません。当社は、過去12か月間に、事業目的または商業目的で以下のカテゴリーの個人情報を第三者に開示しました。',
          [
            'カテゴリー A. 識別子',
            'カテゴリー B. カリフォルニア州顧客記録法に定義される個人情報',
            'カテゴリー G. 位置情報',
            'カテゴリー H. 音声、電子、視覚その他の類似の情報',
            'カテゴリー K. 収集した個人情報から導き出される推論',
            'カテゴリー L. 機微な個人情報（位置情報）'
          ],
          '当社が事業目的または商業目的で個人情報を開示した第三者のカテゴリーについては、「[当社はいつ、誰とお客様の個人情報を共有しますか？](#sharing)」をご覧ください。',
          { h3: 'カリフォルニア州の居住者' },
          'カリフォルニア州民法典第1798.83条（「Shine The Light」法としても知られています）により、カリフォルニア州の居住者である当社のユーザーは、年に1回無料で、当社が直前の暦年にダイレクトマーケティング目的で第三者に開示した個人情報のカテゴリー（もしあれば）に関する情報、ならびに当社が個人情報を共有したすべての第三者の名称および住所を当社に請求し、取得することができます。お客様がカリフォルニア州の居住者であり、このような請求を希望される場合は、下記の連絡先情報を使用して書面で当社に請求を提出してください。',
          'お客様が18歳未満で、カリフォルニア州に居住し、本サービスに登録アカウントをお持ちの場合、お客様が本サービス上で公開した不要なデータの削除を請求する権利があります。このようなデータの削除を請求するには、下記の連絡先情報を使用して当社にご連絡いただき、お客様のアカウントに関連付けられたメールアドレスと、カリフォルニア州に居住している旨の記載を含めてください。当社は、当該データが本サービス上で公開表示されないようにしますが、当該データが当社のすべてのシステム（バックアップなど）から完全または包括的に削除されない場合があることにご留意ください。',
          { h3: 'CCPA プライバシー通知' },
          'このセクションは、カリフォルニア州の居住者にのみ適用されます。カリフォルニア州消費者プライバシー法（CCPA）に基づき、お客様は以下に記載する権利を有します。',
          'カリフォルニア州規則集は、「居住者」を次のように定義しています。',
          [
            '(1) 一時的または通過的な目的以外でカリフォルニア州内にいるすべての個人、および',
            '(2) カリフォルニア州に住所を有し、一時的または通過的な目的でカリフォルニア州外にいるすべての個人'
          ],
          'その他のすべての個人は「非居住者」と定義されます。',
          'この「居住者」の定義がお客様に該当する場合、当社はお客様の個人情報に関して一定の権利と義務を遵守しなければなりません。',
          { h3: 'お客様の個人データに関する権利' },
          '**データの削除を請求する権利 — 削除の請求**',
          'お客様は、自身の個人情報の削除を求めることができます。お客様が当社に個人情報の削除を求めた場合、当社はお客様の請求を尊重し、お客様の個人情報を削除します。ただし、他の消費者による言論の自由の権利の行使、法的義務から生じる当社のコンプライアンス要件、違法行為から保護するために必要となる処理など（ただしこれらに限りません）、法律で定められた一定の例外に従います。',
          '**知らされる権利 — 開示の請求**',
          '状況に応じて、お客様には以下を知る権利があります。',
          [
            '当社がお客様の個人情報を収集・使用しているかどうか',
            '当社が収集する個人情報のカテゴリー',
            '収集した個人情報の利用目的',
            '当社が第三者に個人情報を販売または共有しているかどうか',
            '当社が事業目的で販売、共有または開示した個人情報のカテゴリー',
            '事業目的で個人情報が販売、共有または開示された第三者のカテゴリー',
            '個人情報を収集、販売または共有する事業目的または商業目的',
            '当社がお客様について収集した具体的な個人情報'
          ],
          '適用法に従い、当社は、消費者からの請求に応じて非識別化された消費者情報を提供もしくは削除する義務、または消費者からの請求を確認するために個人データを再識別する義務を負いません。',
          '**消費者のプライバシー権の行使を理由に差別されない権利**',
          'お客様がプライバシー権を行使した場合でも、当社はお客様を差別しません。',
          '**機微な個人情報の使用および開示を制限する権利**',
          'お客様がカリフォルニア州の居住者である場合、機微な個人情報の使用および開示を、平均的な消費者が合理的に期待するサービスの実施または商品の提供に必要な範囲に制限する権利を有します。当社は現在、機微な個人情報（正確な位置情報）を、アプリの主たる想定機能である旅行記録サービスを提供するという中核的な目的のためにのみ使用しています。',
          '**本人確認の手続き**',
          'お客様の請求を受け取った後、当社は、お客様が当社のシステム内に情報を保有している本人と同一人物であることを判断するため、お客様の身元を確認する必要があります。この確認作業のため、当社は、お客様が以前に当社に提供した情報と照合できるよう、情報の提供をお客様に求めることになります。たとえば、お客様が提出する請求の種類に応じて、お客様が提供する情報と当社がすでに保有している情報とを照合できるよう一定の情報の提供を求めたり、お客様が以前に当社に提供した連絡手段（電話やメールなど）でお客様に連絡したりすることがあります。また、状況に応じてその他の確認方法を用いることもあります。',
          '当社は、お客様の請求に含まれる個人情報を、お客様の身元または請求を行う権限を確認するためにのみ使用します。当社は、可能な限り、確認のためにお客様に追加の情報を求めることは避けます。ただし、当社がすでに保有している情報からお客様の身元を確認できない場合、身元確認ならびにセキュリティまたは不正防止の目的で、追加の情報の提供を求めることがあります。このように追加で提供された情報は、お客様の確認が完了し次第、削除します。',
          '**その他のプライバシー上の権利**',
          [
            'お客様は、自身の個人情報の処理に異議を述べることができます。',
            'お客様は、自身の個人データが不正確である場合またはもはや関連性がない場合にその訂正を請求し、または情報の処理の制限を求めることができます。',
            'お客様は、お客様に代わって CCPA に基づく請求を行う授権代理人を指定することができます。当社は、CCPA に従ってお客様に代わって行動する正当な権限を与えられていることの証明を提出しない授権代理人からの請求を拒否することがあります。',
            'お客様は、今後の個人情報の第三者への販売または共有からのオプトアウトを請求することができます。オプトアウトの請求を受けた場合、当社は可能な限り速やかに、遅くとも請求の提出日から15日以内に対応します。'
          ],
          `これらの権利を行使するには、${EMAIL} までメールでご連絡いただくか、本書末尾の連絡先をご参照ください。当社によるお客様のデータの取り扱いについて苦情がある場合は、ぜひお聞かせください。`
        ]
      },
      {
        id: 'updates',
        heading: '13. 当社は本通知を更新することがありますか？',
        blocks: [
          { inShort: 'はい。当社は、関連法令の遵守を維持するため、必要に応じて本通知を更新します。' },
          '当社は、本プライバシー通知を随時更新することがあります。更新版は「最終更新日」の日付の更新によって示され、アクセス可能になった時点で効力を生じます。本プライバシー通知に重要な変更を加える場合、当社は、変更に関する通知を目立つように掲示するか、お客様に直接通知を送信することにより、お客様にお知らせすることがあります。当社がお客様の情報をどのように保護しているかを把握するため、本プライバシー通知を頻繁にご確認いただくことをお勧めします。'
        ]
      },
      {
        id: 'contact',
        heading: '14. 本通知について、どのように当社に問い合わせることができますか？',
        blocks: [
          `本通知についてご質問やご意見がある場合は、${EMAIL} までメールでお問い合わせください。`
        ]
      },
      {
        id: 'review',
        heading: '15. 当社が収集したお客様のデータを、どのように確認、更新または削除できますか？',
        blocks: [
          `お客様の国の適用法に基づき、お客様は、当社がお客様から収集した個人情報へのアクセス、当該情報の変更または削除を請求する権利を有する場合があります。お客様の個人情報の確認、更新または削除を請求するには、${EMAIL} までメールでご連絡ください。`,
          'また、Travo アプリ内で以下の方法により、お客様のデータを直接管理することもできます。',
          [
            'アカウント設定にアクセスしてプロフィール情報を更新する',
            '個々の旅の思い出や写真を削除する',
            'アプリの設定またはデバイスの設定で位置情報の記録を無効にする',
            'アカウントの完全な削除を請求する（お客様のすべてのデータが90日以内に削除されます）'
          ],
          '本プライバシーポリシーは、自動でインテリジェントな旅行記録体験を提供しながら、Travo がお客様の旅行データをどのように取り扱うかについて透明性を確保するために作成されました。当社によるお客様のプライバシーの保護方法についてご質問がある場合は、お気軽にお問い合わせください。'
        ]
      }
    ]
  },

  terms: {
    title: '利用規約',
    updated: '最終更新日：2026年10月25日',
    seo: {
      title: '利用規約 | Travo',
      description:
        'Travo 旅行記録アプリと関連サービスを利用するための法的条件。アカウント、お客様のコンテンツ、禁止行為、当社の責任について定めています。',
      keywords: 'Travo 利用規約, 利用条件, 法的条件, Travo アプリ ライセンス'
    },
    sections: [
      {
        id: 'agreement',
        heading: '本規約への同意',
        blocks: [
          `当社は、Travo Labs として事業を営む ${LEGAL_NAME} です（以下「**当社**」といいます）。`,
          '当社は、Travo モバイルアプリケーション、ならびにこれらの法的条件（以下「**本規約**」といいます）を参照またはリンクするその他の関連製品およびサービス（以下総称して「**本サービス**」といいます）を運営しています。',
          'Travo は、お客様の旅行履歴を自動的に記録・整理するインテリジェントな旅行記録アプリです。本アプリは、バックグラウンドでお客様の位置（都市と国）を自動的に記録し、旅行データを分析し、写真で思い出を作成し、お客様の旅行パターンに基づいて AI による旅行先のおすすめを提供します。',
          `お客様は、${EMAIL} までメールで当社に連絡することができます。`,
          '本規約は、お客様（個人としてか、団体を代表してかを問いません。以下「**お客様**」といいます）と Travo Labs との間で、お客様による本サービスへのアクセスおよびその利用に関して締結される、法的拘束力のある契約を構成します。お客様は、本サービスにアクセスすることにより、本規約のすべてを読み、理解し、これに拘束されることに同意したものとします。お客様が本規約のすべてに同意しない場合、お客様は本サービスの利用を明示的に禁止され、直ちに利用を中止しなければなりません。',
          '本サービス上に随時掲載される補足的な条件または文書は、参照により本規約に明示的に組み込まれます。当社は、当社の単独の裁量により、いつでも、いかなる理由においても、本規約を変更または修正する権利を留保します。当社は、本規約の「最終更新日」を更新することにより変更をお知らせし、お客様は、個々の変更について個別の通知を受け取る権利を放棄します。更新内容を把握するために本規約を定期的に確認することは、お客様の責任です。改訂された本規約が掲載された日以降もお客様が本サービスを継続して利用した場合、お客様は改訂後の本規約の変更に従うものとし、その変更を知らされ、承諾したものとみなされます。',
          '記録のために、本規約の写しを印刷しておくことをお勧めします。'
        ]
      },
      {
        id: 'services',
        heading: '1. 本サービス',
        blocks: [
          '本サービスの利用時に提供される情報は、その配布もしくは利用が法令に反することとなる、または当社が当該法域もしくは国において登録義務を課されることとなる法域または国において、いかなる個人または団体に配布または利用されることも意図していません。したがって、他の地域から本サービスへのアクセスを選択する者は、自らの判断で行うものとし、現地の法律が適用される場合にはその範囲において、現地の法律の遵守について単独で責任を負います。',
          '本サービスは、お客様が旅行履歴を自動的に記録・整理できるよう、個人的かつ非商業的な利用のために提供されます。本サービスは、お客様のデバイスの位置情報サービスを使用して、お客様が訪れた都市と国を記録します。'
        ]
      },
      {
        id: 'ip',
        heading: '2. 知的財産権',
        blocks: [
          { h3: '当社の知的財産' },
          '当社は、本サービス内のすべてのソースコード、データベース、機能、ソフトウェア、アプリケーションデザイン、音声、動画、テキスト、写真およびグラフィック（以下総称して「**コンテンツ**」といいます）、ならびにそこに含まれる商標、サービスマークおよびロゴ（以下「**マーク**」といいます）を含む、本サービスにおけるすべての知的財産権の所有者またはライセンシーです。',
          '当社のコンテンツおよびマークは、米国および世界各国の著作権法および商標法（ならびにその他の各種知的財産権法および不正競争防止法）ならびに条約によって保護されています。',
          'コンテンツおよびマークは、お客様の個人的かつ非商業的な利用のためにのみ、本サービス内でまたは本サービスを通じて「現状有姿」で提供されます。',
          { h3: 'お客様による本サービスの利用' },
          'お客様が下記の「[禁止行為](#prohibited)」セクションを含む本規約を遵守することを条件として、当社はお客様に対し、以下を行うための非独占的、譲渡不能かつ取消可能なライセンスを付与します。',
          ['本サービスにアクセスすること', 'お客様が正当にアクセス権を得たコンテンツの任意の部分の写しを1部ダウンロードまたは印刷すること'],
          'ただし、いずれもお客様の個人的かつ非商業的な利用に限ります。',
          '本セクションまたは本規約の他の箇所に定める場合を除き、本サービスのいかなる部分も、またいかなるコンテンツまたはマークも、当社の事前の書面による明示的な許可なく、いかなる商業目的のためであれ、複製、再製、集約、再公開、アップロード、投稿、公開表示、エンコード、翻訳、送信、配布、販売、ライセンス供与その他の方法で利用することはできません。',
          `本セクションまたは本規約の他の箇所に定める以外の方法で本サービス、コンテンツまたはマークを利用することを希望される場合は、${EMAIL} までご請求ください。当社がお客様に対し、本サービスまたはコンテンツのいずれかの部分の投稿、複製または公開表示の許可を与えた場合、お客様は、当社を本サービス、コンテンツまたはマークの所有者またはライセンサーとして明示し、当社のコンテンツを投稿、複製または表示する際に、著作権表示または権利表示が表示されまたは視認できるようにしなければなりません。`,
          '当社は、本サービス、コンテンツおよびマークについて、お客様に明示的に付与されていないすべての権利を留保します。',
          'これらの知的財産権に対するいかなる違反も、本規約の重大な違反を構成し、お客様が本サービスを利用する権利は直ちに終了します。',
          { h3: 'お客様の提出物およびユーザーコンテンツ' },
          '本サービスを利用する前に、本セクションおよび「[禁止行為](#prohibited)」セクションを注意深くお読みいただき、本サービスを通じてコンテンツを投稿またはアップロードする際に、(a) お客様が当社に付与する権利、および (b) お客様が負う義務をご理解ください。',
          '**提出物：**本サービスに関する質問、コメント、提案、アイデア、フィードバックその他の情報（以下「**提出物**」といいます）を当社に直接送信することにより、お客様は、当該提出物に関するすべての知的財産権を当社に譲渡することに同意するものとします。お客様は、当社が当該提出物を所有し、商業目的その他を問わず、いかなる適法な目的のためにも、お客様への謝辞または対価の支払いなしに、制限なく使用および流布する権利を有することに同意します。',
          '**お客様は、投稿またはアップロードする内容について責任を負います：**本サービスのいずれかの部分を通じて当社に提出物を送信することにより、お客様は以下を行うものとします。',
          [
            '当社の「[禁止行為](#prohibited)」を読み、これに同意したことを確認し、違法、嫌がらせ的、憎悪的、有害、名誉毀損的、わいせつ、いじめ的、虐待的、差別的、いかなる個人もしくは集団に対しても脅迫的、性的に露骨、虚偽、不正確、欺瞞的または誤解を招く提出物を、本サービスを通じて投稿、送信、公開、アップロードまたは伝送しないこと',
            '適用法で認められる範囲において、当該提出物に関する一切の著作者人格権を放棄すること',
            '当該提出物がお客様のオリジナルであること、またはお客様が当該提出物を提出するために必要な権利およびライセンスを有していること、ならびにお客様の提出物に関して上記の権利を当社に付与する完全な権限を有していることを保証すること',
            'お客様の提出物が機密情報に該当しないことを保証し、表明すること'
          ],
          'お客様は、自身の提出物について単独で責任を負い、(a) 本セクション、(b) 第三者の知的財産権、または (c) 適用法へのお客様の違反によって当社が被る可能性のあるあらゆる損失を当社に補償することに明示的に同意します。'
        ]
      },
      {
        id: 'representations',
        heading: '3. ユーザーによる表明',
        blocks: [
          '本サービスを利用することにより、お客様は以下を表明し、保証します。(1) お客様が提出するすべての登録情報が真実、正確、最新かつ完全であること、(2) お客様が当該情報の正確性を維持し、必要に応じて当該登録情報を速やかに更新すること、(3) お客様が法的能力を有し、本規約を遵守することに同意すること、(4) お客様が居住する法域において未成年者ではないこと、(5) ボット、スクリプトその他の方法を問わず、自動化された手段または人間以外の手段で本サービスにアクセスしないこと、(6) 違法または無許可の目的で本サービスを利用しないこと、ならびに (7) お客様による本サービスの利用が適用される法令に違反しないこと。',
          'お客様が虚偽、不正確、最新でない、または不完全な情報を提供した場合、当社は、お客様のアカウントを停止または終了し、本サービス（またはその一部）の現在または将来のあらゆる利用を拒否する権利を有します。'
        ]
      },
      {
        id: 'registration',
        heading: '4. ユーザー登録',
        blocks: [
          '本サービスを利用するには、登録が必要となる場合があります。お客様は、パスワードを秘密に保つことに同意し、お客様のアカウントおよびパスワードのすべての使用について責任を負います。当社は、お客様が選択したユーザー名が不適切、わいせつ、その他好ましくないと当社の単独の裁量により判断した場合、当該ユーザー名を削除、回収または変更する権利を留保します。'
        ]
      },
      {
        id: 'prohibited',
        heading: '5. 禁止行為',
        blocks: [
          'お客様は、当社が本サービスを提供する目的以外の目的で本サービスにアクセスし、または利用することはできません。本サービスは、当社が特に承認または認可したものを除き、いかなる商業的活動に関連しても利用することはできません。',
          '本サービスのユーザーとして、お客様は以下を行わないことに同意します。',
          [
            '当社の書面による許可なく、直接的または間接的に、コレクション、編集物、データベースまたはディレクトリを作成または編纂するために、本サービスからデータその他のコンテンツを体系的に取得すること。',
            '特にユーザーのパスワードなどの機密性の高いアカウント情報を得ようとして、当社および他のユーザーをだまし、欺き、または誤解させること。',
            'コンテンツの使用もしくは複製を防止もしくは制限する機能、または本サービスおよび／もしくはそこに含まれるコンテンツの使用に制限を課す機能を含む、本サービスのセキュリティ関連機能を回避し、無効化し、その他の方法で妨害すること。',
            '当社の見解において、当社および／または本サービスを中傷し、その評判を傷つけ、その他の方法で害すること。',
            '本サービスから取得した情報を、他者への嫌がらせ、虐待または危害のために使用すること。',
            '当社のサポートサービスを不適切に利用すること、または虐待もしくは不正行為に関する虚偽の報告を提出すること。',
            '適用される法令に反する方法で本サービスを利用すること。',
            '本サービスへの無許可のフレーミングまたはリンクを行うこと。',
            'ウイルス、トロイの木馬その他の素材（大文字の過度な使用やスパム行為（反復的なテキストの連続投稿）を含みます）であって、いずれかの当事者による本サービスの中断のない利用および享受を妨げるもの、または本サービスの使用、特徴、機能、運用もしくは保守を改変、損傷、中断、変更もしくは妨害するものをアップロードもしくは送信すること（またはアップロードもしくは送信を試みること）。',
            'スクリプトを使用してコメントやメッセージを送信すること、またはデータマイニング、ロボットその他類似のデータ収集・抽出ツールを使用することなど、システムを自動化された方法で利用すること。',
            'コンテンツから著作権表示その他の権利表示を削除すること。',
            '他のユーザーもしくは他者へのなりすまし、または他のユーザーのユーザー名の使用を試みること。',
            '受動的もしくは能動的な情報収集または送信の仕組みとして機能する素材（透明なグラフィックス交換フォーマット（「gifs」）、1×1 ピクセル、ウェブバグ、Cookie その他類似の装置（「スパイウェア」、「受動的収集メカニズム」または「pcms」と呼ばれることもあります）を含みますが、これらに限りません）をアップロードもしくは送信すること（またはアップロードもしくは送信を試みること）。',
            '本サービス、または本サービスに接続されたネットワークもしくはサービスを妨害し、混乱させ、または過度の負担をかけること。',
            '本サービスのいずれかの部分をお客様に提供することに従事する当社の従業員または代理人に対し、嫌がらせ、迷惑行為、威圧または脅迫を行うこと。',
            '本サービスまたはその一部へのアクセスを防止または制限するために設計された本サービスの措置を回避しようとすること。',
            'Flash、PHP、HTML、JavaScript その他のコードを含みますがこれらに限られない、本サービスのソフトウェアを複製または改変すること。',
            '適用法で認められる場合を除き、本サービスを構成し、またはいかなる形であれその一部を成すソフトウェアを解読、逆コンパイル、逆アセンブルまたはリバースエンジニアリングすること。',
            '標準的な検索エンジンまたはインターネットブラウザの使用の結果として生じる場合を除き、本サービスにアクセスするスパイダー、ロボット、チートツール、スクレイパー、オフラインリーダーを含みますがこれらに限られない自動化システムを使用、起動、開発もしくは配布すること、または無許可のスクリプトその他のソフトウェアを使用もしくは起動すること。',
            '購入代行業者を利用して本サービス上で購入を行うこと。',
            '迷惑メールを送信する目的で電子的その他の手段によりユーザーのユーザー名および／またはメールアドレスを収集すること、または自動化された手段もしくは虚偽の口実によりユーザーアカウントを作成することを含め、本サービスを無許可で利用すること。',
            '当社と競合する取り組みの一環として本サービスを利用すること、またはその他収益を生む活動もしくは商業事業のために本サービスおよび／もしくはコンテンツを利用すること。',
            '旅行統計または位置情報を操作、改ざん、または人為的に水増ししようとすること。',
            'アカウントの認証情報を他者と共有すること、または他者にお客様のアカウントへのアクセスを許可すること。'
          ]
        ]
      },
      {
        id: 'contributions',
        heading: '6. ユーザーによる投稿',
        blocks: [
          '本サービスでは、写真、旅の思い出、コメントその他の素材を含みますがこれらに限られないコンテンツ（以下「**投稿物**」といいます）のアップロード、投稿または共有をお客様にお願いする場合があります。お客様の投稿物は、本サービスの他のユーザーが閲覧できる場合があります。お客様が投稿物を作成し、または利用可能にする場合、お客様はこれにより以下を表明し、保証します。',
          [
            'お客様の投稿物の作成、配布、送信、公開表示もしくは実演、ならびにアクセス、ダウンロードまたは複製が、第三者の著作権、特許権、商標権、営業秘密または著作者人格権を含みますがこれらに限られない財産的権利を侵害しておらず、今後も侵害しないこと。',
            'お客様が投稿物の作成者かつ所有者であるか、または、本サービスおよび本規約が想定するあらゆる方法でお客様の投稿物を使用し、当社、本サービスおよび本サービスの他のユーザーにお客様の投稿物の使用を許可するために必要なライセンス、権利、同意、権利放棄および許可を有していること。',
            '本サービスおよび本規約が想定するあらゆる方法でお客様の投稿物を掲載し使用できるようにするため、お客様の投稿物に含まれる識別可能なすべての個人から、その氏名または肖像を使用するための書面による同意、権利放棄および／または許可を得ていること。',
            'お客様の投稿物が虚偽、不正確または誤解を招くものではないこと。',
            'お客様の投稿物が、求められていないまたは無許可の広告、販促資料、ねずみ講、チェーンレター、スパム、大量送信メールその他の形態の勧誘ではないこと。',
            'お客様の投稿物が、わいせつ、卑猥、好色、不潔、暴力的、嫌がらせ的、文書による名誉毀損、口頭による名誉毀損、その他（当社の判断により）好ましくないものではないこと。',
            'お客様の投稿物が、誰かを嘲笑、愚弄、中傷、威圧または虐待するものではないこと。',
            'お客様の投稿物が、他者への嫌がらせもしくは脅迫（これらの用語の法的な意味において）、ならびに特定の個人もしくは集団に対する暴力の助長のために使用されないこと。',
            'お客様の投稿物が、適用される法律、規則または規程に違反しないこと。',
            'お客様の投稿物が、第三者のプライバシー権またはパブリシティ権を侵害しないこと。',
            'お客様の投稿物が、児童ポルノに関する適用法、その他未成年者の健康または福祉の保護を目的とする適用法に違反しないこと。',
            'お客様の投稿物に、人種、出身国、性別、性的指向または身体障害に関連する不快なコメントが含まれていないこと。',
            'お客様の投稿物が、本規約のいずれかの条項または適用される法令に違反しておらず、また違反する素材にリンクしていないこと。'
          ],
          '前記に違反する本サービスの利用は本規約に違反するものであり、とりわけ、お客様が本サービスを利用する権利の終了または停止につながる可能性があります。'
        ]
      },
      {
        id: 'contribution-license',
        heading: '7. 投稿物のライセンス',
        blocks: [
          '本サービスのいずれかの部分に投稿物を投稿することにより、お客様は、当社に対し、商業目的、広告目的その他いかなる目的のためにも、当該投稿物（お客様の画像および音声を含みますが、これらに限りません）をホスト、使用、複製、再製、開示、販売、再販売、公開、放送、改題、アーカイブ、保存、キャッシュ、公に上演、公開表示、再フォーマット、翻訳、送信、抜粋（全部または一部）および配布し、当該投稿物の二次的著作物を作成し、もしくは他の著作物に組み込み、ならびに前記についてサブライセンスを付与し許諾する、無制限、無期限、取消不能、永続的、非独占的、譲渡可能、ロイヤルティ無料、全額支払済みの世界的な権利およびライセンスを自動的に付与し、またそれを付与する権利を有することを表明し保証します。この使用および配布は、あらゆるメディア形式およびあらゆるメディアチャネルを通じて行われる場合があります。',
          'このライセンスは、現在知られている、または今後開発されるあらゆる形式、メディアまたは技術に適用され、該当する場合には、当社によるお客様の氏名、会社名およびフランチャイズ名、ならびにお客様が提供する商標、サービスマーク、商号、ロゴ、個人的および商業的な画像の使用を含みます。お客様は、お客様の投稿物に関するすべての著作者人格権を放棄し、お客様の投稿物について著作者人格権がその他の方法で主張されていないことを保証します。',
          '当社は、お客様の投稿物に対していかなる所有権も主張しません。お客様は、すべての投稿物ならびにお客様の投稿物に関連する知的財産権その他の財産的権利の完全な所有権を保持します。当社は、本サービスのいずれの領域においても、お客様が提供した投稿物内の記述または表明について責任を負いません。お客様は、本サービスへのお客様の投稿物について単独で責任を負い、お客様の投稿物に関して、当社をあらゆる責任から免除し、当社に対するいかなる法的措置も控えることに明示的に同意します。',
          '当社は、当社の単独かつ絶対的な裁量により、(1) 投稿物を編集し、墨消しし、その他の方法で変更する権利、(2) 投稿物を本サービス上のより適切な場所に配置するために再分類する権利、ならびに (3) いつでも、いかなる理由においても、通知なしに投稿物を事前審査または削除する権利を有します。当社は、お客様の投稿物を監視する義務を負いません。'
        ]
      },
      {
        id: 'app-license',
        heading: '8. モバイルアプリケーションのライセンス',
        blocks: [
          { h3: '使用許諾' },
          'お客様がモバイルアプリケーションを通じて本サービスにアクセスする場合、当社はお客様に対し、お客様が所有または管理する無線電子デバイスにモバイルアプリケーションをインストールして使用し、本規約に含まれる本モバイルアプリケーションライセンスの条件に厳密に従って当該デバイス上でモバイルアプリケーションにアクセスし使用する、取消可能、非独占的、譲渡不能かつ限定的な権利を付与します。お客様は、以下を行ってはなりません。',
          [
            '適用法で認められる場合を除き、アプリケーションを逆コンパイル、リバースエンジニアリングもしくは逆アセンブルし、そのソースコードの導出を試み、または暗号を解除すること',
            'アプリケーションを修正、改変、改良、強化もしくは翻訳し、またはその二次的著作物を作成すること',
            'アプリケーションへのアクセスまたはその使用に関連して、適用される法律、規則または規程に違反すること',
            '当社またはアプリケーションのライセンサーが掲示した権利表示（著作権または商標の表示を含みます）を削除、変更または不明瞭にすること',
            '収益を生む活動、商業事業、その他アプリケーションが設計または意図されていない目的のためにアプリケーションを使用すること',
            '複数のデバイスまたはユーザーが同時にアクセスまたは使用することを可能にするネットワークその他の環境上でアプリケーションを利用可能にすること',
            '直接的または間接的に、アプリケーションと競合し、またはいかなる形であれアプリケーションの代替となる製品、サービスまたはソフトウェアを作成するためにアプリケーションを使用すること',
            'アプリケーションを使用して、いずれかのウェブサイトに自動化されたクエリを送信し、または迷惑な商業メールを送信すること',
            'アプリケーションで使用するためのアプリケーション、アクセサリまたはデバイスの設計、開発、製造、ライセンス供与または配布において、当社の専有情報、インターフェースその他の知的財産を使用すること'
          ],
          { h3: 'Apple および Android デバイス' },
          '以下の条件は、App Store または Google Play（それぞれを「**アプリ配信事業者**」といいます）から入手したモバイルアプリケーションを使用して本サービスにアクセスする場合に適用されます。',
          [
            '当社のモバイルアプリケーションについてお客様に付与されるライセンスは、該当する Apple iOS または Android オペレーティングシステムを使用するデバイス上で、該当するアプリ配信事業者の利用規約に定める使用規則に従ってアプリケーションを使用する、譲渡不能なライセンスに限定されます。',
            '当社は、本規約に含まれる本モバイルアプリケーションライセンスの条件に定めるとおり、またはその他適用法で求められるとおり、モバイルアプリケーションに関する保守およびサポートサービスを提供する責任を負います。お客様は、各アプリ配信事業者がモバイルアプリケーションに関する保守およびサポートサービスを提供する義務を一切負わないことを認めます。',
            'モバイルアプリケーションが適用される保証に適合しない場合、お客様は該当するアプリ配信事業者に通知することができ、当該アプリ配信事業者は、その規約および方針に従い、モバイルアプリケーションについて支払われた購入代金（もしあれば）を返金することがあります。また、適用法で認められる最大限の範囲において、アプリ配信事業者はモバイルアプリケーションに関してその他いかなる保証義務も負いません。',
            'お客様は、(i) 米国政府による禁輸措置の対象となっている国、または米国政府により「テロ支援」国家に指定されている国に所在していないこと、および (ii) 米国政府の禁止または制限対象者リストに掲載されていないことを表明し、保証します。',
            'お客様は、モバイルアプリケーションを使用する際に、適用される第三者の契約条件を遵守しなければなりません。たとえば、VoIP アプリケーションをお持ちの場合、モバイルアプリケーションを使用する際に、その無線データサービス契約に違反してはなりません。',
            'お客様は、アプリ配信事業者が本規約に含まれる本モバイルアプリケーションライセンスの条件の第三者受益者であること、および各アプリ配信事業者が、その第三者受益者として、本規約に含まれる本モバイルアプリケーションライセンスの条件をお客様に対して執行する権利を有する（かつ当該権利を受諾したものとみなされる）ことを認め、同意します。'
          ]
        ]
      },
      {
        id: 'management',
        heading: '9. 本サービスの管理',
        blocks: [
          '当社は、以下を行う権利を留保しますが、義務を負うものではありません。(1) 本規約の違反がないか本サービスを監視すること、(2) 当社の単独の裁量により法律または本規約に違反したと判断した者に対し、当該ユーザーを法執行機関に通報することを含みますがこれに限られない、適切な法的措置を講じること、(3) 当社の単独の裁量により、制限なく、お客様の投稿物またはその一部を拒否し、アクセスを制限し、利用可能性を制限し、または（技術的に可能な範囲で）無効化すること、(4) 当社の単独の裁量により、制限、通知または責任なく、サイズが過大である、またはいかなる形であれ当社のシステムに負担をかけるすべてのファイルおよびコンテンツを本サービスから削除し、またはその他の方法で無効化すること、ならびに (5) その他、当社の権利および財産を保護し、本サービスの適切な機能を促進するよう設計された方法で本サービスを管理すること。'
        ]
      },
      {
        id: 'privacy',
        heading: '10. プライバシーポリシー',
        blocks: [
          '当社は、データのプライバシーとセキュリティを重視しています。当社の[プライバシーポリシー](/privacy)をご確認ください。本サービスを利用することにより、お客様は、本規約に組み込まれている当社のプライバシーポリシーに拘束されることに同意するものとします。本サービスは米国でホスティングされていることにご留意ください。米国の適用法とは異なる個人データの収集、使用または開示に関する法律その他の要件を有する世界の他の地域から本サービスにアクセスする場合、お客様は本サービスを継続して利用することにより、お客様のデータを米国に移転することになり、お客様のデータが米国に移転され、米国で処理されることに明示的に同意するものとします。',
          'さらに、当社は、子どもから故意に情報を受け取り、要求し、もしくは求めること、または子どもを対象に故意にマーケティングを行うことはありません。したがって、米国の児童オンラインプライバシー保護法（COPPA）に従い、13歳未満の者が必要かつ検証可能な保護者の同意なしに当社に個人情報を提供したことを当社が実際に知った場合、当社は合理的に実行可能な限り速やかに当該情報を本サービスから削除します。'
        ]
      },
      {
        id: 'termination',
        heading: '11. 期間および終了',
        blocks: [
          '本規約は、お客様が本サービスを利用している間、完全な効力を有します。本規約の他のいかなる規定も制限することなく、当社は、当社の単独の裁量により、通知または責任なく、本規約に含まれる表明、保証もしくは誓約、または適用される法令への違反を含みますがこれに限られない、いかなる理由によりまたは理由なく、いかなる者に対しても本サービスへのアクセスおよびその利用を拒否する（特定の IP アドレスのブロックを含みます）権利を留保します。当社は、当社の単独の裁量により、いつでも警告なく、お客様による本サービスの利用もしくは本サービスへの参加を終了し、またはお客様のアカウントおよびお客様が投稿したコンテンツもしくは情報を削除することができます。',
          '当社が理由のいかんを問わずお客様のアカウントを終了または停止した場合、お客様は、たとえ第三者に代わって行動している場合であっても、お客様の名前、偽名もしくは借用した名前、または第三者の名前で新たなアカウントを登録および作成することを禁止されます。当社は、お客様のアカウントの終了または停止に加えて、民事上、刑事上および差止めによる救済の追求を含みますがこれに限られない、適切な法的措置を講じる権利を留保します。'
        ]
      },
      {
        id: 'modifications',
        heading: '12. 変更および中断',
        blocks: [
          '当社は、当社の単独の裁量により、いつでも、いかなる理由においても、通知なく、本サービスのコンテンツを変更、修正または削除する権利を留保します。ただし、当社は、本サービス上の情報を更新する義務を負いません。当社は、本サービスの変更、価格の変更、停止または中止について、お客様または第三者に対して責任を負いません。',
          '当社は、本サービスが常に利用可能であることを保証することはできません。当社は、ハードウェア、ソフトウェアその他の問題が発生し、または本サービスに関連する保守を行う必要が生じ、その結果、中断、遅延またはエラーが生じることがあります。当社は、いつでも、いかなる理由においても、お客様に通知することなく、本サービスを変更、改訂、更新、停止、中止その他の方法で修正する権利を留保します。お客様は、本サービスのダウンタイムまたは中止の間にお客様が本サービスにアクセスし、または利用できないことにより生じたいかなる損失、損害または不便についても、当社が一切責任を負わないことに同意します。本規約のいかなる規定も、当社に本サービスを維持およびサポートする義務、またはそれに関連する修正、アップデートもしくはリリースを提供する義務を課すものと解釈されないものとします。'
        ]
      },
      {
        id: 'law',
        heading: '13. 準拠法',
        blocks: [
          '本規約は、台湾（中華民国）法に準拠します。お客様が居住する国の消費者保護法により、お客様に当該国で請求を提起する権利が与えられている場合を除き、本規約に関連して生じうるいかなる紛争についても、台湾台北地方法院が第一審の管轄権を有します。'
        ]
      },
      {
        id: 'disputes',
        heading: '14. 紛争解決',
        blocks: [
          { h3: '非公式な交渉' },
          'お客様または当社のいずれかが提起する本規約に関連する紛争、論争または請求（以下、個別に、また総称して「**紛争**」といいます）の解決を迅速化し、その費用を抑制するため、お客様および当社（以下、個別に「**当事者**」、総称して「**両当事者**」といいます）は、訴訟手続を開始する前に、まず少なくとも30日間、紛争（下記に明示的に定める紛争を除きます）について非公式に交渉を試みることに同意します。当該非公式な交渉は、一方の当事者から他方の当事者への書面による通知をもって開始されます。',
          { h3: '非公式な交渉の例外' },
          '両当事者は、以下の紛争が、非公式な交渉に関する上記の規定の対象とならないことに同意します。(a) 当事者の知的財産権の執行もしくは保護を求める紛争、またはその有効性に関する紛争、(b) 窃盗、海賊行為、プライバシーの侵害または無許可の使用の申立てに関連し、またはそれらから生じる紛争、ならびに (c) 差止めによる救済を求める請求。'
        ]
      },
      {
        id: 'corrections',
        heading: '15. 訂正',
        blocks: [
          '本サービス上の情報には、説明、価格、在庫状況その他の各種情報を含め、誤植、不正確な記載または記載漏れが含まれている場合があります。当社は、いつでも事前の通知なく、誤り、不正確な記載または記載漏れを訂正し、本サービス上の情報を変更または更新する権利を留保します。'
        ]
      },
      {
        id: 'disclaimer',
        heading: '16. 免責事項',
        blocks: [
          '本サービスは「現状有姿」かつ「提供可能な範囲」で提供されます。お客様は、本サービスの利用がお客様ご自身の単独の責任において行われることに同意します。法律で認められる最大限の範囲において、当社は、本サービスおよびお客様によるその利用に関して、商品性、特定目的への適合性および非侵害の黙示の保証を含みますがこれらに限られない、明示または黙示を問わずすべての保証を否認します。当社は、本サービスのコンテンツ、または本サービスにリンクされたウェブサイトもしくはモバイルアプリケーションのコンテンツの正確性または完全性について、いかなる保証または表明も行わず、以下について一切の責任を負いません。(1) コンテンツおよび素材の誤り、間違いまたは不正確さ、(2) お客様による本サービスへのアクセスおよびその利用から生じる、性質のいかんを問わない人身傷害または財産的損害、(3) 当社の安全なサーバーおよび／またはそこに保存されている一切の個人情報および／もしくは財務情報への無許可のアクセスまたはその使用、(4) 本サービスとの間の送信の中断または停止、(5) 第三者によって本サービスに、または本サービスを通じて送信される可能性のあるバグ、ウイルス、トロイの木馬その他これらに類するもの、および／または (6) コンテンツおよび素材の誤りもしくは脱落、または本サービスを介して投稿、送信その他の方法で利用可能になったコンテンツの使用の結果として生じたあらゆる種類の損失もしくは損害。',
          '当社は、本サービス、ハイパーリンクされたウェブサイト、またはバナーその他の広告に掲載されたウェブサイトもしくはモバイルアプリケーションを通じて第三者が広告または提供する製品またはサービスについて、保証、推奨もしくは請け合いを行わず、またはその責任を負いません。当社は、お客様と製品またはサービスの第三者提供者との間の取引の当事者とはならず、またその取引を監視する責任を一切負いません。',
          'いかなる媒体またはいかなる環境を通じた製品またはサービスの購入の場合と同様に、お客様は最善の判断を行い、必要に応じて注意を払うべきです。',
          '**位置情報に関する免責事項：**本サービスは、旅行記録機能を提供するために位置情報に依拠しています。当社は、位置情報の正確性、完全性または信頼性を保証しません。位置情報の記録は、すべての地域または条件において正常に機能するとは限りません。お客様は、位置情報に基づく機能には固有の制約および不正確さが存在する可能性があることを認めます。'
        ]
      },
      {
        id: 'liability',
        heading: '17. 責任の制限',
        blocks: [
          'いかなる場合においても、当社または当社の取締役、従業員もしくは代理人は、お客様による本サービスの利用から生じる逸失利益、収益の損失、データの損失その他の損害を含む、直接損害、間接損害、結果損害、懲戒的損害、付随的損害、特別損害または懲罰的損害について、たとえ当社がそのような損害の可能性について知らされていた場合であっても、お客様または第三者に対して責任を負いません。',
          '本規約にこれと異なる定めがある場合でも、原因のいかんおよび訴訟の形式にかかわらず、お客様に対する当社の責任は、常に、訴訟原因が発生する前の6か月間にお客様が当社に支払った金額（もしあれば）を上限とします。',
          '米国の一部の州法および国際法では、黙示の保証の制限、または特定の損害の除外もしくは制限が認められていません。これらの法律がお客様に適用される場合、上記の免責または制限の一部または全部がお客様に適用されないことがあり、お客様は追加の権利を有する場合があります。'
        ]
      },
      {
        id: 'indemnification',
        heading: '18. 補償',
        blocks: [
          'お客様は、(1) お客様の投稿物、(2) 本サービスの利用、(3) 本規約の違反、(4) 本規約に定めるお客様の表明および保証の違反、(5) 知的財産権を含みますがこれに限られない第三者の権利のお客様による侵害、または (6) お客様が本サービスを介してつながった本サービスの他のユーザーに対する明白な有害行為に起因または関連して第三者が行う、合理的な弁護士費用および経費を含むあらゆる損失、損害、責任、請求または要求について、当社（当社の子会社、関連会社、ならびにそれぞれの役員、代理人、パートナーおよび従業員を含みます）を防御し、補償し、免責することに同意します。',
          '前記にかかわらず、当社は、お客様の費用負担において、お客様が当社を補償する義務を負う事項について、排他的な防御および管理を引き受ける権利を留保し、お客様は、お客様の費用負担において、当該請求に対する当社の防御に協力することに同意します。当社は、本補償の対象となる請求、訴訟または手続きを知った場合、合理的な努力をもってお客様に通知します。'
        ]
      },
      {
        id: 'user-data',
        heading: '19. ユーザーデータ',
        blocks: [
          '当社は、本サービスのパフォーマンスを管理する目的で、お客様が本サービスに送信する一定のデータ、ならびにお客様による本サービスの利用に関するデータを保持します。当社は定期的にデータのバックアップを行っていますが、お客様が送信するすべてのデータ、またはお客様が本サービスを使用して行った活動に関連するすべてのデータについては、お客様が単独で責任を負います。',
          'お客様は、当該データの損失または破損について当社がお客様に対して一切責任を負わないことに同意し、ここに、当該データの損失または破損から生じる当社に対するいかなる訴権も放棄します。',
          'お客様は、本サービスを通じていつでも旅行データをエクスポートすることができます。大切な旅の思い出や写真は、定期的にバックアップすることをお勧めします。'
        ]
      },
      {
        id: 'electronic',
        heading: '20. 電子的な通信、取引および署名',
        blocks: [
          '本サービスの訪問、当社へのメールの送信、およびオンラインフォームの入力は、電子的な通信に該当します。お客様は、電子的な通信を受け取ることに同意し、当社がメールおよび本サービス上で電子的にお客様に提供するすべての契約、通知、開示その他の通信が、当該通信を書面で行うべきとする法的要件を満たすことに同意します。',
          'お客様は、ここに、電子署名、電子契約、電子注文その他の電子記録の使用、ならびに当社によりまたは本サービスを介して開始または完了された取引の通知、方針および記録の電子的な交付に同意します。お客様は、ここに、いずれかの法域における法令、規則、規程、条例その他の法律に基づく、原本の署名もしくは非電子的な記録の交付もしくは保持、または電子的手段以外の手段による支払いもしくはクレジットの付与を要求する権利または要件を放棄します。'
        ]
      },
      {
        id: 'misc',
        heading: '21. 雑則',
        blocks: [
          '本規約、および本サービス上でまたは本サービスに関して当社が掲載する方針または運用規則は、お客様と当社との間の完全な合意および了解を構成します。当社が本規約のいずれかの権利または規定を行使または執行しなかった場合でも、当該権利または規定の放棄とはみなされません。',
          '本規約は、法律で認められる最大限の範囲で効力を有します。当社は、いつでも、当社の権利および義務の全部または一部を他者に譲渡することができます。当社は、当社の合理的な支配を超える原因により生じた損失、損害、遅延または不履行について責任を負いません。',
          '本規約のいずれかの条項またはその一部が違法、無効または執行不能と判断された場合、当該条項またはその一部は本規約から分離可能なものとみなされ、残りの条項の有効性および執行可能性に影響を与えません。',
          '本規約または本サービスの利用の結果として、お客様と当社との間に合弁事業、パートナーシップ、雇用または代理の関係が生じることはありません。お客様は、本規約が当社によって起草されたことを理由に当社に不利に解釈されないことに同意します。お客様は、ここに、本規約が電子的な形式であること、および本規約の締結にあたり当事者が署名していないことに基づいてお客様が有する可能性のある一切の抗弁を放棄します。'
        ]
      },
      {
        id: 'contact',
        heading: '22. お問い合わせ',
        blocks: [
          '本サービスに関する苦情の解決、または本サービスの利用に関する詳細情報については、以下までお問い合わせください。',
          `**${LEGAL_NAME}（Travo Labs として事業を運営）** — メール：${EMAIL}`
        ]
      }
    ]
  }
};
